import {
  createPasskey,
  getPasskey,
  passkeysSupported,
  type SecondFactor,
  type StaffMe,
  type TotpEnrollBeginResponse,
} from '@central/api';
import { RecoveryCodeList, TotpQr, color, font } from '@central/ui';
import { Button, TextInput } from '@mantine/core';
import { useState } from 'react';
import { useApi } from '../api';
import type { FactorStep } from '../lib/signInSteps';
import { errorMessage } from '../lib/errors';
import { ErrorText, LinkButton, LinkRow, Panel, TrustBrowser } from './signInParts';
import { amberButton, bodyText, codeInputStyles, inputStyles, quietButton } from './signInStyles';

/** Runs one async step with a busy flag and an inline error. */
function useStep() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, setError, run };
}

type Mode = 'totp' | 'recovery' | 'passkey';

/**
 * The second sign-in step for a user who has a factor: a TOTP code, a passkey,
 * or (when methods lists "recovery") a recovery code.
 */
export function VerifyStep({
  methods,
  step,
  of,
  onSignedIn,
  onStartOver,
}: {
  methods: SecondFactor[];
  step: number;
  of: number;
  onSignedIn: (me: StaffMe) => Promise<void> | void;
  onStartOver: () => void;
}) {
  const api = useApi();
  const { busy, error, setError, run } = useStep();
  const [mode, setMode] = useState<Mode>(
    methods.includes('totp') ? 'totp' : methods.includes('webauthn') ? 'passkey' : 'recovery',
  );
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(false);
  const canPasskey = passkeysSupported() && methods.includes('webauthn');

  const submit = () =>
    run(async () => {
      try {
        const me =
          mode === 'totp'
            ? await api.auth.verifyTotp({ code: code.replace(/\s/g, ''), trustBrowser: trust })
            : await api.auth.verifyRecoveryCode({ code: code.trim(), trustBrowser: trust });
        await onSignedIn(me);
      } finally {
        setCode('');
      }
    });

  const passkey = () =>
    run(async () => {
      const { publicKey } = await api.auth.webauthnLoginBegin();
      const credential = await getPasskey(publicKey);
      await onSignedIn(await api.auth.webauthnLoginFinish({ credential, trustBrowser: trust }));
    });

  const switchMode = (m: Mode) => {
    setError(undefined);
    setCode('');
    setMode(m);
  };

  return (
    <Panel
      step={step}
      of={of}
      title="Verify it’s you"
      onSubmit={() => void (mode === 'passkey' ? canPasskey && passkey() : submit())}
    >
      {mode === 'passkey' ? (
        <p style={bodyText}>
          {canPasskey
            ? 'Confirm with your passkey.'
            : 'This browser cannot use passkeys. Use another way below, or a browser that can.'}
        </p>
      ) : (
        <>
          <p style={bodyText}>
            {mode === 'totp'
              ? 'Enter the 6-digit code from your authenticator app.'
              : 'Enter one of your recovery codes. Each code works once.'}
          </p>
          <TextInput
            label={mode === 'totp' ? 'Code' : 'Recovery code'}
            inputMode={mode === 'totp' ? 'numeric' : 'text'}
            autoComplete="one-time-code"
            required
            autoFocus
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            styles={codeInputStyles}
          />
        </>
      )}
      <TrustBrowser checked={trust} onChange={setTrust} />
      <ErrorText>{error}</ErrorText>
      {mode !== 'passkey' ? (
        <Button type="submit" fullWidth h={44} loading={busy} style={amberButton}>
          Verify and sign in
        </Button>
      ) : (
        canPasskey && (
          <Button type="submit" fullWidth h={44} loading={busy} style={amberButton}>
            Use my passkey
          </Button>
        )
      )}
      <LinkRow>
        <LinkButton onClick={onStartOver}>Start over</LinkButton>
        {mode !== 'recovery' && methods.includes('recovery') && (
          <LinkButton onClick={() => switchMode('recovery')}>Use a recovery code</LinkButton>
        )}
        {mode !== 'totp' && methods.includes('totp') && (
          <LinkButton onClick={() => switchMode('totp')}>Use your authenticator app</LinkButton>
        )}
        {mode !== 'passkey' && canPasskey && (
          <LinkButton onClick={() => switchMode('passkey')}>Use a passkey instead</LinkButton>
        )}
      </LinkRow>
    </Panel>
  );
}

/**
 * Enrollment of a first second factor on an mfa-stage session (a new user, or
 * one whose factors were reset). Finishing promotes the session and returns
 * StaffMe.
 */
export function EnrollStep({
  step,
  of,
  onEnrolled,
  onStartOver,
}: {
  step: number;
  of: number;
  onEnrolled: (me: StaffMe) => Promise<void> | void;
  onStartOver: () => void;
}) {
  const api = useApi();
  const { busy, error, setError, run } = useStep();
  const canPasskey = passkeysSupported();
  const [method, setMethod] = useState<'passkey' | 'totp'>(canPasskey ? 'passkey' : 'totp');
  const [name, setName] = useState('');
  const [secret, setSecret] = useState<TotpEnrollBeginResponse>();
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(false);

  const registerPasskey = () =>
    run(async () => {
      const { publicKey } = await api.auth.webauthnRegisterBegin();
      const credential = await createPasskey(publicKey);
      await onEnrolled(
        await api.auth.webauthnRegisterFinish({
          credential,
          name: name.trim() || 'Passkey',
          trustBrowser: trust,
        }),
      );
    });

  const beginTotp = () => run(async () => setSecret(await api.auth.totpEnrollBegin()));

  const finishTotp = () =>
    run(async () => {
      try {
        await onEnrolled(
          await api.auth.totpEnrollFinish({ code: code.replace(/\s/g, ''), trustBrowser: trust }),
        );
      } finally {
        setCode('');
      }
    });

  const choose = (m: 'passkey' | 'totp') => {
    setError(undefined);
    setMethod(m);
  };

  return (
    <Panel
      step={step}
      of={of}
      title="Set up a second factor"
      onSubmit={() =>
        void (method === 'passkey' ? registerPasskey() : secret ? finishTotp() : beginTotp())
      }
    >
      <p style={bodyText}>
        Staff accounts need a passkey or an authenticator app. You will use it every time you sign
        in.
      </p>
      {method === 'passkey' ? (
        <>
          <TextInput
            label="Passkey name"
            placeholder="e.g. Work laptop"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            styles={inputStyles}
          />
          <TrustBrowser checked={trust} onChange={setTrust} />
          <ErrorText>{error}</ErrorText>
          <Button type="submit" fullWidth h={44} loading={busy} style={amberButton}>
            Create a passkey
          </Button>
        </>
      ) : !secret ? (
        <>
          <ErrorText>{error}</ErrorText>
          <Button type="submit" fullWidth h={44} loading={busy} style={amberButton}>
            Set up an authenticator app
          </Button>
        </>
      ) : (
        <>
          <p style={bodyText}>
            Scan this code with your authenticator app, or enter the key by hand. It is shown only
            once.
          </p>
          <div style={{ alignSelf: 'center' }}>
            <TotpQr value={secret.otpauthUrl} />
          </div>
          <code
            data-testid="totp-secret"
            style={{
              fontFamily: font.mono,
              fontSize: 14,
              background: color.signInPage,
              border: `1px solid ${color.signInInputBorder}`,
              borderRadius: 8,
              padding: '8px 12px',
              wordBreak: 'break-all',
              color: color.signInHeading,
            }}
          >
            {secret.secret}
          </code>
          <TextInput
            label="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            styles={codeInputStyles}
          />
          <TrustBrowser checked={trust} onChange={setTrust} />
          <ErrorText>{error}</ErrorText>
          <Button type="submit" fullWidth h={44} loading={busy} style={amberButton}>
            Turn on and sign in
          </Button>
        </>
      )}
      <LinkRow>
        <LinkButton onClick={onStartOver}>Start over</LinkButton>
        {method === 'passkey' ? (
          <LinkButton onClick={() => choose('totp')}>Use an authenticator app instead</LinkButton>
        ) : (
          canPasskey &&
          !secret && (
            <LinkButton onClick={() => choose('passkey')}>Use a passkey instead</LinkButton>
          )
        )}
      </LinkRow>
    </Panel>
  );
}

/**
 * After a first factor: create recovery codes now, while the session has just
 * proved a second factor. Skipping is allowed; codes can be made later.
 */
export function RecoveryCodesStep({
  step,
  of,
  onDone,
}: {
  step: number;
  of: number;
  onDone: () => Promise<void> | void;
}) {
  const api = useApi();
  const { busy, error, run } = useStep();
  const [codes, setCodes] = useState<string[]>();

  const generate = () => run(async () => setCodes((await api.auth.generateRecoveryCodes()).codes));

  return (
    <Panel step={step} of={of} title="Save recovery codes">
      <p style={bodyText}>
        A recovery code signs you in once if you lose your passkey or authenticator app. Store them
        somewhere safe; they are shown only once.
      </p>
      {codes ? (
        <>
          <RecoveryCodeList codes={codes} dark />
          <Button type="button" fullWidth h={44} style={amberButton} onClick={() => void onDone()}>
            I’ve saved them, continue
          </Button>
        </>
      ) : (
        <>
          <ErrorText>{error}</ErrorText>
          <Button
            type="button"
            fullWidth
            h={44}
            loading={busy}
            style={amberButton}
            onClick={() => void generate()}
          >
            Create recovery codes
          </Button>
          <Button type="button" fullWidth h={44} style={quietButton} onClick={() => void onDone()}>
            Skip for now
          </Button>
        </>
      )}
    </Panel>
  );
}

/**
 * The steps after the password, shared by sign-in and /setup. `done` is how
 * many steps are already behind the user.
 */
export function FactorSteps({
  step,
  done,
  onStep,
  onStartOver,
  onSignedIn,
}: {
  step: FactorStep;
  done: number;
  onStep: (s: FactorStep) => void;
  onStartOver: () => void;
  onSignedIn: (me?: StaffMe) => Promise<void>;
}) {
  switch (step.kind) {
    case 'verify':
      return (
        <VerifyStep
          methods={step.methods}
          step={done + 1}
          of={done + 1}
          onSignedIn={onSignedIn}
          onStartOver={onStartOver}
        />
      );
    case 'enroll':
      return (
        <EnrollStep
          step={done + 1}
          of={done + 2}
          onEnrolled={(me) => onStep({ kind: 'codes', me })}
          onStartOver={onStartOver}
        />
      );
    case 'codes':
      return <RecoveryCodesStep step={done + 2} of={done + 2} onDone={() => onSignedIn(step.me)} />;
  }
}
